import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { timeout } from 'rxjs/operators';
import { LoaderService } from 'src/app/components/loader/loader.service';
import { ToastService } from 'src/app/components/toast-notification/toast.service';
import { URL } from 'src/app/constants/urls.constants';

/** Max file size 200 MB per API (api-upload-file.md). */
const MAX_UPLOAD_SIZE_BYTES = 200 * 1024 * 1024;
/** Timeout for upload (video compression can take 5–10 min). */
const UPLOAD_TIMEOUT_MS = 10 * 60 * 1000;

export interface UploadApiResponse {
  statusCode: number;
  message: string;
  data: string | { storageUrl: string; [key: string]: any };
}

@Injectable({
  providedIn: 'root'
})
export class AzureFileUploadService {

  constructor(
    private $loader: LoaderService,
    private $toast: ToastService,
    private $http: HttpClient
  ) { }

  /**
   * Upload a single file (image or video) via the common upload API.
   * - Shows full-screen loader until upload finishes.
   * - For video: also shows a toast on the left "Video uploading... This may take a few minutes."
   * - API compresses videos (H.264/AAC, max 1080p) and uploads to Azure Blob.
   * Caller (e.g. create-post) waits for this to complete, then calls post API to create the post.
   */
  async uploadFile(file: File): Promise<{ Location: string; type: string }> {
    if (!file) {
      return Promise.reject(new Error('No file provided'));
    }
    if (file.size > MAX_UPLOAD_SIZE_BYTES) {
      return Promise.reject(new Error(`File exceeds 200 MB limit (${(file.size / 1024 / 1024).toFixed(1)} MB).`));
    }

    const formData = new FormData();
    formData.append('image', file);

    const isVideo = file.type && file.type.startsWith('video');
    this.$loader.show();
    if (isVideo) {
      this.$toast.infoLeft(
        'Video uploading... This may take a few minutes for large files. Post will be created when upload is done.'
      );
    }

    try {
      const resp = await this.$http
        .post<UploadApiResponse>(URL.uploadChatImage, formData, {
          withCredentials: true
        })
        .pipe(timeout(UPLOAD_TIMEOUT_MS))
        .toPromise();

      if (!resp || resp.statusCode !== 200 || resp.data == null) {
        const msg = (resp && (resp as any).message) || 'Upload failed';
        return Promise.reject(new Error(msg));
      }

      const url = typeof resp.data === 'string' ? resp.data : resp.data.storageUrl;
      if (!url) {
        return Promise.reject(new Error('No URL in response'));
      }
      return { Location: url, type: file.type };
    } catch (err) {
      const message = err && err.error && err.error.message ? err.error.message : (err.message || 'Upload failed');
      return Promise.reject(new Error(message));
    } finally {
      this.$loader.hide();
    }
  }

  async uploadFiles(files: File[]): Promise<Array<{ Location: string; type: string }>> {
    try {
      const results = await Promise.all(files.map(f => this.uploadFile(f)));
      return results;
    } catch (err) {
      return Promise.reject(err);
    }
  }
}
